import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  X, 
  Camera,
  MapPin,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useCreateListing, useUpdateListing, useListing } from '@/hooks/useForex';
import { supportedCurrencies, nigerianCities } from '@/mocks/forexData';

interface CurrencyRate {
  from: string;
  to: string;
  buyRate: number;
  sellRate: number;
  minAmount: number;
  maxAmount: number;
}

const ListingFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!id;

  const { listing: existingListing, isLoading: isLoadingListing } = useListing(id || '');
  const { createListing, isCreating } = useCreateListing();
  const { updateListing, isUpdating } = useUpdateListing();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [currencies, setCurrencies] = useState<CurrencyRate[]>([
    { from: 'USD', to: 'NGN', buyRate: 1500, sellRate: 1550, minAmount: 100, maxAmount: 10000 }
  ]);
  const [images, setImages] = useState<string[]>([]);

  // Load existing data when editing
  useEffect(() => {
    if (existingListing) {
      setTitle(existingListing.title);
      setDescription(existingListing.description);
      setAddress(existingListing.location.address);
      setCity(existingListing.location.city);
      setState(existingListing.location.state);
      setCurrencies(existingListing.currencies);
      setImages(existingListing.images);
    }
  }, [existingListing]);

  const addCurrency = () => {
    setCurrencies([...currencies, { 
      from: 'USD', 
      to: 'NGN', 
      buyRate: 0, 
      sellRate: 0, 
      minAmount: 100, 
      maxAmount: 10000 
    }]);
  };

  const removeCurrency = (index: number) => {
    setCurrencies(currencies.filter((_, i) => i !== index));
  };

  const updateCurrency = (index: number, field: keyof CurrencyRate, value: string | number) => {
    const updated = [...currencies];
    updated[index] = { ...updated[index], [field]: value };
    setCurrencies(updated);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      // Simulate image upload with placeholder URLs
      const newImages = Array.from(files).map((_, i) => 
        `https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop&random=${Date.now() + i}`
      );
      setImages([...images, ...newImages]);
    }
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title || !description || !address || !city || !state) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    if (currencies.length === 0) {
      toast({
        title: 'No currencies',
        description: 'Please add at least one currency pair.',
        variant: 'destructive',
      });
      return;
    }

    const listingData = {
      title,
      description,
      location: {
        address,
        city,
        state,
        coordinates: {
          lat: nigerianCities.find(c => c.name === city)?.lat || 6.5244,
          lng: nigerianCities.find(c => c.name === city)?.lng || 3.3792,
        },
      },
      currencies,
      images: images.length > 0 ? images : ['https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop'],
    };

    try {
      if (isEditing && id) {
        await updateListing({ id, data: listingData });
        toast({
          title: 'Listing updated',
          description: 'Your listing has been updated successfully.',
        });
      } else {
        await createListing(listingData);
        toast({
          title: 'Listing created',
          description: 'Your listing has been created and is pending approval.',
        });
      }
      navigate('/forex/vendor/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isEditing && isLoadingListing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">
              {isEditing ? 'Edit Listing' : 'Create New Listing'}
            </h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="container mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Listing Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Best USD Exchange in Lagos"
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your services, special offers, etc."
                  rows={4}
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="e.g., 15 Broad Street"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <select
                    id="city"
                    value={city}
                    onChange={(e) => {
                      const selectedCity = nigerianCities.find(c => c.name === e.target.value);
                      setCity(e.target.value);
                      setState(selectedCity?.state || '');
                    }}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    required
                  >
                    <option value="">Select city</option>
                    {nigerianCities.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={state}
                    readOnly
                    placeholder="Auto-filled"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Exchange Rates */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Exchange Rates
              </CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addCurrency}>
                <Plus className="w-4 h-4 mr-1" />
                Add Currency
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {currencies.map((currency, index) => (
                <div key={index} className="border rounded-lg p-4 relative">
                  {currencies.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={() => removeCurrency(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>From</Label>
                      <select
                        value={currency.from}
                        onChange={(e) => updateCurrency(index, 'from', e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      >
                        {supportedCurrencies.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.code}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>To</Label>
                      <select
                        value={currency.to}
                        onChange={(e) => updateCurrency(index, 'to', e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      >
                        {supportedCurrencies.filter(c => c.code !== currency.from).map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.code}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Buy Rate (₦)</Label>
                      <Input
                        type="number"
                        value={currency.buyRate}
                        onChange={(e) => updateCurrency(index, 'buyRate', parseFloat(e.target.value))}
                        placeholder="1500"
                      />
                    </div>
                    <div>
                      <Label>Sell Rate (₦)</Label>
                      <Input
                        type="number"
                        value={currency.sellRate}
                        onChange={(e) => updateCurrency(index, 'sellRate', parseFloat(e.target.value))}
                        placeholder="1550"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Min Amount</Label>
                      <Input
                        type="number"
                        value={currency.minAmount}
                        onChange={(e) => updateCurrency(index, 'minAmount', parseFloat(e.target.value))}
                      />
                    </div>
                    <div>
                      <Label>Max Amount</Label>
                      <Input
                        type="number"
                        value={currency.maxAmount}
                        onChange={(e) => updateCurrency(index, 'maxAmount', parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Images */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Photos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                {images.map((image, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={image} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 w-6 h-6"
                      onClick={() => removeImage(index)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <label className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/25 flex flex-col items-center justify-center cursor-pointer hover:bg-accent">
                  <Plus className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground">Add Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isCreating || isUpdating}
            >
              {isCreating || isUpdating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>{isEditing ? 'Update Listing' : 'Create Listing'}</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ListingFormPage;
